import { useEffect, useRef, useState, type CSSProperties, type PointerEvent, type ReactNode } from 'react'
import { IconPlus } from './Icons'
import { LogoMark } from './Logo'
import { useKeyboardOpen } from '../lib/viewport'
import { useIsDesktop } from '../lib/touch'
import { useAddButton } from '../lib/addButton'
import { openSheet } from '../lib/router'
import { haptic } from '../lib/haptics'
import { guardHoldRelease } from '../lib/holdRelease'

const HOLD_MS = 500
/** How long the Terrazzo mark shows on the button before the menu opens over it. */
const LOGO_BEAT_MS = 140
const RING = 2 * Math.PI * 32

/**
 * The "+" on Board and Days (SPEC §8.4). On desktop it floats in the corner; on phones it's the
 * bar's + (beside the bar), so here it only tells the bar what a tap makes.
 */
export function Fab({ onClick }: { onClick: () => void }) {
  const desktop = useIsDesktop()
  const kb = useKeyboardOpen()
  useAddButton(onClick)
  if (!desktop || kb) return null
  return <AddButton onClick={onClick} className="fixed right-8 z-20 h-14 w-14 shadow-lg" style={{ bottom: '5rem' }} />
}

/**
 * A "+". Tap: `onClick`. Press and hold: a ring fills, the + turns into the Terrazzo mark and the
 * Space menu (the command box) opens right then, while still holding. Right-click on desktop opens
 * it straight away. `children`: drawn on it (the phone bar's new-activity dot).
 */
export function AddButton({
  onClick,
  onHold = () => openSheet('command'),
  label = 'Add',
  className = '',
  style,
  children,
}: {
  onClick: () => void
  onHold?: () => void
  label?: string
  className?: string
  style?: CSSProperties
  children?: ReactNode
}) {
  const [phase, setPhase] = useState<'idle' | 'holding' | 'armed'>('idle')
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const beat = useRef<ReturnType<typeof setTimeout> | null>(null)
  const start = useRef<{ x: number; y: number } | null>(null)
  const opened = useRef(false)
  const pointerType = useRef('mouse')

  const clear = () => {
    if (timer.current) clearTimeout(timer.current)
    if (beat.current) clearTimeout(beat.current)
    timer.current = null
    beat.current = null
    start.current = null
  }
  useEffect(() => clear, [])

  const reset = () => {
    clear()
    setPhase('idle')
  }
  const open = () => {
    if (opened.current) return
    clear()
    opened.current = true
    // With a mouse the release lands on the menu's backdrop, not here: reset the button then.
    guardHoldRelease({ onRelease: () => setPhase('idle'), refocus: true })
    onHold()
  }
  const arm = () => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
    if (beat.current || opened.current) return
    setPhase('armed')
    haptic(12)
    beat.current = setTimeout(open, LOGO_BEAT_MS)
  }
  const down = (e: PointerEvent) => {
    pointerType.current = e.pointerType
    if (e.button !== 0) return
    clear()
    opened.current = false
    start.current = { x: e.clientX, y: e.clientY }
    setPhase('holding')
    timer.current = setTimeout(arm, HOLD_MS)
  }
  const move = (e: PointerEvent) => {
    const s = start.current
    if (s && !beat.current && Math.hypot(e.clientX - s.x, e.clientY - s.y) > 12) reset()
  }
  const up = () => {
    // Let go during the logo beat: open now (the release handler still catches this release).
    if (beat.current) open()
    reset()
  }

  return (
    <button
      type="button"
      onClick={() => {
        if (opened.current) {
          opened.current = false
          return
        }
        onClick()
      }}
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerLeave={() => {
        if (!beat.current && !opened.current) reset()
      }}
      onPointerCancel={reset}
      onContextMenu={(e) => {
        e.preventDefault()
        // Android fires this on a long press, around when our own timer would.
        if (pointerType.current !== 'mouse') {
          if (phase === 'holding') arm()
          return
        }
        reset()
        onHold()
      }}
      aria-label={label}
      title={`${label} · hold for the command menu`}
      className={`flex flex-none items-center justify-center rounded-full bg-fg text-bg transition-transform duration-150 select-none touch-none ${className} ${
        phase === 'armed' ? 'scale-110' : phase === 'holding' ? 'scale-95' : 'active:scale-95'
      }`}
      style={{ ...style, WebkitTouchCallout: 'none' }}
    >
      {/* The hold ring, drawn just outside the button. */}
      <svg className="pointer-events-none absolute -inset-1.5 -rotate-90" viewBox="0 0 68 68" aria-hidden="true">
        <circle
          cx="34"
          cy="34"
          r="32"
          fill="none"
          stroke="var(--fg)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeDasharray={RING}
          strokeDashoffset={phase === 'idle' ? RING : 0}
          style={{ transition: phase === 'holding' ? `stroke-dashoffset ${HOLD_MS}ms linear` : 'none', opacity: phase === 'idle' ? 0 : 1 }}
        />
      </svg>
      <span className={`transition-transform duration-200 ${phase === 'armed' ? 'scale-110' : ''}`}>{phase === 'armed' ? <LogoMark size={26} /> : <IconPlus size={24} />}</span>
      {children}
    </button>
  )
}
